import { Link } from "react-router-dom";
import { Info, ArrowRight, Camera } from "lucide-react";
import Seo from "../components/Seo";
import { useI18n } from "../i18n/LanguageProvider";
import { useRegisterAlternates } from "../components/layout/Layout";
import { pagePath, entityPath } from "../config/pages";
import { getTreatment } from "../data/treatments";
import { FX, formatVND, toUSD, toAUD, IMPLANT_PRICES, IMPLANT_EXTRAS, CROWN_PRICES, VENEER_PRICES } from "../config/pricing";
import { PageHero, CTABand } from "../components/Sections";
import { Reveal } from "../components/motion/Reveal";
import Technology from "../components/Technology";
import { webPageSchema, breadcrumbSchema } from "../lib/schema";

const L = (en, fr, de) => ({ en, fr, de });

const META = {
  title: L(
    "Dental Prices in Vietnam | Indicative Implant, Crown & Veneer Costs",
    "Prix des soins dentaires au Vietnam | Coûts indicatifs implants, couronnes, facettes",
    "Zahnbehandlung Vietnam Kosten | Richtpreise Implantate, Kronen, Veneers"
  ),
  desc: L(
    "Indicative dental prices in Vietnam from selected partner providers — implants, crowns and veneers in VND, USD and AUD. Final costs depend on your case.",
    "Prix dentaires indicatifs au Vietnam de prestataires sélectionnés — implants, couronnes et facettes en VND, USD et AUD. Le coût final dépend de votre cas.",
    "Indikative Zahnpreise in Vietnam von ausgewählten Anbietern — Implantate, Kronen und Veneers in VND, USD und AUD. Endkosten hängen vom Fall ab."
  ),
  h1: L("Dental Prices in Vietnam", "Prix des soins dentaires au Vietnam", "Zahnbehandlung in Vietnam: Kosten"),
  intro: L(
    "Indicative pricing from selected Dentel Vietnam partner providers, to help international patients plan. Prices are shown in VND (source of truth) with approximate USD and AUD conversions.",
    "Tarifs indicatifs de prestataires partenaires sélectionnés de Dentel Vietnam, pour aider les patients internationaux à planifier. Les prix sont en VND (référence) avec des conversions approximatives en USD et AUD.",
    "Indikative Preise ausgewählter Dentel-Vietnam-Partneranbieter, zur Planungshilfe für internationale Patienten. Preise in VND (Referenz) mit ungefähren USD- und AUD-Umrechnungen."
  ),
};

const TXT = {
  system: L("System / Material", "Système / Matériau", "System / Material"),
  perImplant: L("Indicative price per implant from a selected partner provider.", "Prix indicatif par implant d’un prestataire sélectionné.", "Richtpreis pro Implantat von einem ausgewählten Anbieter."),
  perTooth: L("Indicative price per tooth from a selected partner provider.", "Prix indicatif par dent d’un prestataire sélectionné.", "Richtpreis pro Zahn von einem ausgewählten Anbieter."),
  implants: L("Dental Implants", "Implants dentaires", "Zahnimplantate"),
  crowns: L("Dental Crowns", "Couronnes dentaires", "Zahnkronen"),
  veneers: L("Dental Veneers", "Facettes dentaires", "Veneers"),
  approxUSD: L("Approx. USD", "USD approx.", "ca. USD"),
  approxAUD: L("Approx. AUD", "AUD approx.", "ca. AUD"),
  boneNote: L("Bone grafting is only recommended when clinically indicated.", "La greffe osseuse n’est recommandée que si elle est cliniquement indiquée.", "Ein Knochenaufbau wird nur bei klinischer Indikation empfohlen."),
  fxNote: L(
    "Foreign-currency amounts are approximate and shown for convenience. Exchange rates change over time. Treatment is quoted according to the provider’s applicable pricing and final treatment plan.",
    "Les montants en devises étrangères sont approximatifs et fournis à titre indicatif. Les taux de change évoluent. Le traitement est facturé selon la tarification applicable du prestataire et le plan de traitement final.",
    "Fremdwährungsbeträge sind ungefähr und dienen der Bequemlichkeit. Wechselkurse ändern sich. Die Behandlung wird gemäß den geltenden Preisen des Anbieters und dem finalen Behandlungsplan berechnet."
  ),
  fxUpdated: L("Currency conversion reference updated:", "Référence de conversion mise à jour :", "Währungsumrechnung aktualisiert:"),
  disclaimer: L(
    "Prices shown are indicative examples from selected Dentel Vietnam partner providers and are not a final quotation or guarantee. Final treatment costs depend on clinical examination, dental X-rays or CBCT, diagnosis, treatment complexity, materials, implant system, bone condition and any additional procedures required.",
    "Les prix affichés sont des exemples indicatifs de prestataires partenaires sélectionnés de Dentel Vietnam et ne constituent ni un devis final ni une garantie. Le coût final dépend de l’examen clinique, des radiographies ou du CBCT, du diagnostic, de la complexité, des matériaux, du système d’implant, de l’état osseux et des procédures complémentaires.",
    "Die angezeigten Preise sind indikative Beispiele ausgewählter Dentel-Vietnam-Partneranbieter und keine endgültige Kostenschätzung oder Garantie. Die Endkosten hängen von klinischer Untersuchung, Röntgen/CBCT, Diagnose, Komplexität, Materialien, Implantatsystem, Knochenzustand und Zusatzeingriffen ab."
  ),
  helpNote: L(
    "Dentel Vietnam helps international patients obtain an individual treatment plan and quotation from an appropriate licensed dental provider.",
    "Dentel Vietnam aide les patients internationaux à obtenir un plan de traitement et un devis individuels auprès d’un prestataire dentaire agréé approprié.",
    "Dentel Vietnam hilft internationalen Patienten, einen individuellen Behandlungsplan und ein Angebot von einem geeigneten lizenzierten Anbieter zu erhalten."
  ),
  sendXray: L("Send My X-ray / Dental Photos", "Envoyer ma radio / mes photos", "Röntgen / Fotos senden"),
  warranty: L(
    "Warranty periods, coverage and conditions vary by provider, implant system, restoration material and treatment, and should be confirmed in writing as part of the individual treatment plan.",
    "Les durées, la couverture et les conditions de garantie varient selon le prestataire, le système d’implant, le matériau et le traitement, et doivent être confirmées par écrit dans le plan de traitement individuel.",
    "Garantiezeiten, -umfang und -bedingungen variieren je nach Anbieter, Implantatsystem, Material und Behandlung und sollten schriftlich im individuellen Behandlungsplan bestätigt werden."
  ),
};

function PriceTable({ title, rows, caption, lang }) {
  return (
    <div className="mt-10 first:mt-0">
      <h2 className="text-2xl sm:text-3xl text-navy">{title}</h2>
      <div className="mt-5 overflow-x-auto rounded-3xl border border-navy/10 bg-white shadow-soft">
        <table className="w-full min-w-[520px] text-left">
          <thead>
            <tr className="border-b border-navy/10 bg-mist text-xs font-semibold uppercase tracking-wide text-navy/60">
              <th className="px-5 py-4">{TXT.system[lang]}</th>
              <th className="px-5 py-4 text-right">VND</th>
              <th className="px-5 py-4 text-right">{TXT.approxUSD[lang]}</th>
              <th className="px-5 py-4 text-right">{TXT.approxAUD[lang]}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-navy/6 last:border-0 hover:bg-mist/50 transition-colors">
                <td className="px-5 py-4 font-semibold text-navy">{r.name}</td>
                <td className="px-5 py-4 text-right font-semibold text-navy">{formatVND(r.vnd)}</td>
                <td className="px-5 py-4 text-right text-navy/70">{toUSD(r.vnd)}</td>
                <td className="px-5 py-4 text-right text-navy/70">{toAUD(r.vnd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {caption && <p className="mt-3 text-sm text-navy/55">{caption}</p>}
    </div>
  );
}

export default function Prices() {
  const { t, lang, p } = useI18n();
  const alternates = { en: pagePath("prices", "en"), fr: pagePath("prices", "fr"), de: pagePath("prices", "de") };
  useRegisterAlternates(alternates);
  const path = pagePath("prices", lang);
  const title = META.h1[lang];
  const implantsLink = entityPath(null, getTreatment("dental-implants").slug, lang);
  const crownsLink = entityPath(null, getTreatment("dental-crowns").slug, lang);
  const veneersLink = entityPath(null, getTreatment("porcelain-veneers").slug, lang);

  return (
    <>
      <Seo
        title={META.title[lang]}
        description={META.desc[lang]}
        path={path}
        alternates={alternates}
        lang={lang}
        jsonLd={[
          webPageSchema({ name: title, description: META.desc[lang], path }),
          breadcrumbSchema([{ name: t.common.home, path: p("home") }, { name: title, path }]),
        ]}
      />
      <PageHero eyebrow={t.nav.prices} title={title} intro={META.intro[lang]} crumbs={[{ name: t.nav.prices, to: path }]} />

      <section className="container-dv py-16 lg:py-20">
        <Reveal>
          <div className="mb-8 flex flex-wrap items-center gap-3 rounded-2xl border border-dental/15 bg-dental/5 px-5 py-4 text-sm text-navy/70">
            <Info className="h-5 w-5 shrink-0 text-dental" />
            <span>{TXT.fxNote[lang]}</span>
            <span className="ml-auto rounded-full bg-white px-3 py-1 text-xs font-semibold text-dental">
              {TXT.fxUpdated[lang]} {FX.referenceUpdated}
            </span>
          </div>
        </Reveal>

        <Reveal>
          <PriceTable title={<Link to={implantsLink} className="hover:text-dental transition-colors">{TXT.implants[lang]}</Link>} rows={IMPLANT_PRICES} caption={TXT.perImplant[lang]} lang={lang} />
          <div className="mt-4 overflow-x-auto rounded-2xl border border-navy/10 bg-white shadow-soft">
            <table className="w-full min-w-[520px] text-left">
              <tbody>
                {IMPLANT_EXTRAS.map((r, i) => (
                  <tr key={i}>
                    <td className="px-5 py-4 font-semibold text-navy">{r.name}</td>
                    <td className="px-5 py-4 text-right font-semibold text-navy">{formatVND(r.vnd)}</td>
                    <td className="px-5 py-4 text-right text-navy/70">{toUSD(r.vnd)}</td>
                    <td className="px-5 py-4 text-right text-navy/70">{toAUD(r.vnd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-sm text-navy/55">{TXT.boneNote[lang]}</p>
        </Reveal>

        <Reveal delay={0.05}>
          <PriceTable title={<Link to={crownsLink} className="hover:text-dental transition-colors">{TXT.crowns[lang]}</Link>} rows={CROWN_PRICES} caption={TXT.perTooth[lang]} lang={lang} />
        </Reveal>
        <Reveal delay={0.05}>
          <PriceTable title={<Link to={veneersLink} className="hover:text-dental transition-colors">{TXT.veneers[lang]}</Link>} rows={VENEER_PRICES} caption={TXT.perTooth[lang]} lang={lang} />
        </Reveal>

        {/* Disclaimers */}
        <Reveal delay={0.05}>
          <div className="mt-10 space-y-4">
            <div className="flex gap-3 rounded-2xl border border-navy/10 bg-mist p-5 text-sm leading-relaxed text-navy/70">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-dental" />
              <span>{TXT.disclaimer[lang]}</span>
            </div>
            <p className="text-sm leading-relaxed text-navy/65">{TXT.warranty[lang]}</p>
            <p className="text-[15px] font-semibold text-navy">{TXT.helpNote[lang]}</p>
          </div>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link to={p("contact")} data-testid="prices-cta" className="inline-flex items-center justify-center gap-2 rounded-full bg-dental px-7 py-4 text-base font-semibold text-white hover:bg-dental-dark transition-colors">
              {t.cta.myFreePlan}
              <ArrowRight className="h-5 w-5" />
            </Link>
            <Link to={p("contact")} data-testid="prices-xray-cta" className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-navy/15 px-7 py-4 text-base font-semibold text-navy hover:border-turquoise hover:text-turquoise transition-colors">
              <Camera className="h-5 w-5" />
              {TXT.sendXray[lang]}
            </Link>
          </div>
        </Reveal>
      </section>

      <div className="bg-mist">
        <Technology />
      </div>

      <CTABand />
    </>
  );
}
